import type { FC, Ref } from "react";
import { useId, useRef, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import type { Track } from "../../../../collaboration/src/shared/types/worksheet";

import { LabeledFileUploadController } from "./LabeledFileUploadController";
import { LabeledSelect } from "./LabeledSelect";

import { INTERFACE_LANGUAGES, Trans, t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { IconText } from "mds/components/IconText";
import { TextField } from "mds/components/TextField";
import { LinkButton } from "mds/components/buttons/LinkButton";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import {
  AddPlusIcon,
  ChevronDownIcon,
  LoadingDotsIcon,
  PaperClipLinkIcon,
  TrashDeleteIcon,
} from "mds/icons";
import { isValidVideoCaptionFile } from "utils/video-caption-validation";

interface VideoTracksFormProps {
  className?: string;
  defaultTracks?: Track[];
  onSubmit: (data: { tracks: Track[] }) => void;
  onCancel: () => void;
  ref?: Ref<HTMLFormElement>;
}

interface FormValues {
  tracks: {
    src: string;
    srcLang: Track["srcLang"] | "";
    label: string;
    kind: Track["kind"] | "";
  }[];
}

const KIND_OPTIONS = ["subtitles", "captions", "descriptions"] as const;
const LANGUAGE_OPTIONS = INTERFACE_LANGUAGES;

const VideoTracksForm: FC<VideoTracksFormProps> = ({
  className,
  defaultTracks,
  onSubmit,
  onCancel,
  ref,
}) => {
  const id = useId();
  const form = useForm<FormValues>({
    defaultValues: {
      tracks: defaultTracks && defaultTracks.length ? defaultTracks : [],
    },
    mode: "onBlur",
  });
  const {
    register,
    control,
    watch,
    formState: { isDirty },
  } = form;
  const {
    fields,
    append: appendTrack,
    remove: removeTrack,
  } = useFieldArray({
    name: "tracks",
    control,
  });

  const listRef = useRef<HTMLOListElement | null>(null);
  const trackValues = watch("tracks");
  const [openPanelIndex, setOpenPanelIndex] = useState<number | null>(null);

  const preprocessSubmit = (data: FormValues) => {
    const preprocessedTracks: Track[] = data.tracks.flatMap((track) => {
      if (!track.src) {
        return [];
      }
      return {
        ...track,
        label:
          track.label ||
          (track.srcLang ? t(`language.${track.srcLang}`) : t("asset_field.tracks.new_track")),
      } as Track;
    });
    onSubmit({ ...data, tracks: preprocessedTracks });
  };

  return (
    <FormProvider {...form}>
      <form
        className={twMerge("flex flex-col gap-4", className)}
        ref={ref}
        data-stay-visible
        onSubmit={form.handleSubmit(preprocessSubmit)}
      >
        <h2 className="text-base font-medium text-black-tint-10">
          {t("asset_field.tracks.title")}
        </h2>
        <p className="text-sm font-normal text-black-tint-55">
          {t("asset_field.tracks.description")}
        </p>
        {fields.length > 0 && (
          <ol className="flex flex-col gap-2 overflow-auto" ref={listRef}>
            {fields.map((field, index) => {
              if (!trackValues) return null; // TypeScript guard
              const isOpen = openPanelIndex === index;
              const panelId = `${id}-panel-${index}`;

              return (
                <li
                  className="flex flex-col gap-2 rounded-md border border-black-tint-90 supports-squircle:rounded-xl supports-squircle:corner-squircle"
                  key={field.id}
                >
                  <button
                    aria-controls={isOpen ? panelId : undefined}
                    aria-expanded={isOpen}
                    className={twMerge(
                      "group flex flex-row items-center justify-between p-6",
                      isOpen && "pb-4",
                    )}
                    data-open={isOpen || undefined}
                    type="button"
                    onClick={() => {
                      if (openPanelIndex === index) {
                        setOpenPanelIndex(null);
                      } else {
                        setOpenPanelIndex(index);
                      }
                    }}
                  >
                    <div className="text-left">
                      <div className="text-sm font-medium text-black-tint-20">
                        {trackValues[index].label || t("asset_field.tracks.new_track")}
                      </div>
                      <div className="text-xs font-normal text-black-tint-55">
                        {KIND_OPTIONS.includes(
                          trackValues[index].kind as (typeof KIND_OPTIONS)[number],
                        )
                          ? t(
                              `asset_field.tracks.${trackValues[index].kind as (typeof KIND_OPTIONS)[number]}`,
                            )
                          : t("asset_field.tracks.no_type")}{" "}
                        ·{" "}
                        {LANGUAGE_OPTIONS.includes(
                          trackValues[index].srcLang as (typeof LANGUAGE_OPTIONS)[number],
                        )
                          ? t(
                              `language.${trackValues[index].srcLang as (typeof LANGUAGE_OPTIONS)[number]}`,
                            )
                          : t("asset_field.tracks.no_language")}
                      </div>
                    </div>
                    <ChevronDownIcon className="group-data-[open]:rotate-180" />
                  </button>
                  {openPanelIndex === index && (
                    <div className="grid w-full grid-cols-2 gap-4 px-6 pb-6" id={panelId}>
                      <div className="col-span-2">
                        {/*
                          We set `required` on `<FormField>` so that the label field visually appears to be required,
                          but we don't set it on the `register` call because we want to allow the user to leave it blank and default to the language name.
                        */}
                        <FormField
                          label={t("asset_field.tracks.label")}
                          name={`tracks.${index}.label`}
                          required
                        >
                          {(controlProps) => (
                            <TextField
                              {...register(`tracks.${index}.label` as const)}
                              {...controlProps}
                              placeholder={t("asset_field.tracks.label_placeholder")}
                            />
                          )}
                        </FormField>
                      </div>
                      <div>
                        <LabeledSelect
                          label={t("asset_field.tracks.kind")}
                          required
                          {...register(`tracks.${index}.kind` as const, {
                            required: t("error.field_required"),
                            validate: (value) =>
                              KIND_OPTIONS.includes(value as (typeof KIND_OPTIONS)[number])
                                ? true
                                : t("error.invalid_type"),
                          })}
                        >
                          <option value="">{t("asset_field.tracks.select_kind")}</option>
                          {KIND_OPTIONS.map((kind) => (
                            <option key={kind} value={kind}>
                              {t(`asset_field.tracks.${kind}`)}
                            </option>
                          ))}
                        </LabeledSelect>
                      </div>
                      <div>
                        <LabeledSelect
                          label={t("asset_field.tracks.srcLang")}
                          required
                          {...register(`tracks.${index}.srcLang` as const, {
                            required: t("error.field_required"),
                            validate: (value) =>
                              LANGUAGE_OPTIONS.includes(value as (typeof LANGUAGE_OPTIONS)[number])
                                ? true
                                : t("error.invalid_language"),
                          })}
                        >
                          <option value="">{t("asset_field.tracks.select_srcLang")}</option>
                          {LANGUAGE_OPTIONS.map((lang) => (
                            <option key={lang} value={lang}>
                              {t(`language.${lang}`)}
                            </option>
                          ))}
                        </LabeledSelect>
                      </div>
                      <div className="col-span-2">
                        <LabeledFileUploadController
                          accept="text/vtt,.vtt"
                          label={t("asset_field.tracks.src")}
                          name={`tracks.${index}.src`}
                          validateFiles={async (files) => {
                            const fileArray = Array.from(files);
                            if (fileArray.some((file) => file.type !== "text/vtt")) {
                              return t("asset_field.tracks.only_vtt_files");
                            }
                            if (fileArray.length !== 1) {
                              return t("error.only_one_file_allowed");
                            }
                            if (!(await isValidVideoCaptionFile(fileArray[0]))) {
                              return t("asset_field.tracks.invalid_vtt");
                            }
                          }}
                          required
                        >
                          {({ value, select, remove, isUploading }) =>
                            isUploading ? (
                              <div className="flex cursor-default flex-row items-center justify-center gap-1 rounded border border-dashed border-blue-tint-20 bg-blue-tint-90 p-5 text-sm text-black-tint-20">
                                <LoadingDotsIcon aria-label={t("common.uploading")} />
                              </div>
                            ) : value ? (
                              <div className="flex flex-row items-center gap-3 rounded-lg border border-black-tint-90 p-4">
                                <IconText
                                  className="grow gap-1 truncate"
                                  iconStart={<PaperClipLinkIcon />}
                                  text={<span className="truncate">{value.split("/").pop()}</span>}
                                />
                                <SquareIconButton
                                  aria-label={t("asset_field.tracks.remove_file")}
                                  kind="tertiary"
                                  size="s"
                                  onClick={remove}
                                >
                                  <TrashDeleteIcon />
                                </SquareIconButton>
                              </div>
                            ) : (
                              <div
                                className={
                                  "flex cursor-default flex-row items-center justify-center gap-1 rounded border border-dashed border-blue-tint-20 bg-blue-tint-95 p-5 text-sm text-black-tint-40 transition-[border] duration-[0.24s] ease-in-out " +
                                  "hover:bg-blue-tint-90 group-data-[drag-hover]:bg-blue-tint-90"
                                }
                                onClick={select}
                              >
                                <Trans i18nKey="asset_field.tracks.drop_or_upload_vtt" t={t}>
                                  <span>Drop or </span>
                                  <LinkButton
                                    size="s"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      select();
                                    }}
                                  >
                                    upload a WebVTT (.vtt) file
                                  </LinkButton>
                                </Trans>
                              </div>
                            )
                          }
                        </LabeledFileUploadController>
                      </div>
                      <div className="col-span-2">
                        <SquareButton
                          kind="destructive"
                          size="m"
                          onClick={() => {
                            removeTrack(index);
                            if (openPanelIndex === index) {
                              setOpenPanelIndex(null);
                            }
                          }}
                        >
                          <IconText
                            iconStart={<TrashDeleteIcon />}
                            text={t("asset_field.tracks.delete_track")}
                          />
                        </SquareButton>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
        <div className="flex flex-row justify-between">
          <SquareButton
            kind="ghost"
            size="m"
            onClick={() => {
              appendTrack({
                src: "",
                srcLang: "",
                label: "",
                kind: "",
              });
              setOpenPanelIndex(fields.length);
              // scroll list to the bottom, *after* the new track is rendered
              setTimeout(() => {
                listRef.current?.lastElementChild?.scrollIntoView({
                  block: "end",
                  behavior: "smooth",
                });
              }, 0);
            }}
          >
            <IconText iconStart={<AddPlusIcon />} text={t("asset_field.tracks.add_track")} />
          </SquareButton>
          <div className="flex flex-row-reverse gap-2">
            <SquareButton disabled={!isDirty} kind="accent" size="m" type="submit">
              {t("common.save")}
            </SquareButton>
            <SquareButton kind="tertiary" size="m" onClick={onCancel}>
              {t("common.cancel")}
            </SquareButton>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

// Using a default export to allow lazy loading
// eslint-disable-next-line import/no-default-export
export default VideoTracksForm;
